// rideSync.ts
// ============
//
// Wave 14cg Part 2: cross-device-Sync für Rides (GPS-aufgezeichnete Touren).
//
// Bisher lebten Rides nur in IDB pro Browser-Installation — beim Gerät-
// wechsel oder Storage-Clear war die History weg. Mit dieser Pipeline geht
// jede beendete Ride zusätzlich zum Backend, und ein neues Gerät kann nach
// dem Login alle Rides ziehen.
//
// Architektur (analog cruxQueue / elevationQueue / adminPoiQueue):
//
//   rideRecorder.stop()
//     → IDB rides[id]   (full ride mit endedAt, updatedAt)
//     → enqueueRidePush(id)    (rideQueue[id] = { op:'put', queuedAt })
//     → flushRideQueueSoon()
//
//   flushRideQueue()
//     - iteriert über alle rideQueue-Einträge
//     - 'put':    PUT /api/me/rides/:id mit Stats-Aggregat
//                 → bei 200: setRideSyncedAt(id, now), delete queueEntry
//                 → bei 401: silent skip (User nicht eingeloggt, retry on next login)
//                 → bei 4xx (außer 401): markFailure + lassen (kein blind retry)
//                 → bei 5xx/network: markFailure, retry on next online-tick
//     - 'delete': DELETE /api/me/rides/:id
//                 → bei 200 oder 404: delete queueEntry (idempotent)
//                 → bei 5xx/network: markFailure, lassen
//
//   pullRides()       — wird beim App-Start nach erfolgreichem Auth-Check
//                       gerufen, holt alle Server-Rides und merged in IDB:
//                       Server-Ride existiert lokal nicht → einfügen.
//                       Server-Ride existiert lokal → mtime-newer-wins
//                       (Server gewinnt bei tie, weil Server-Stand authority).
//                       Lokale Rides ohne Server-Pendant → bleiben (werden
//                       beim nächsten Flush hochgepusht).

import { getDb } from './storage/db';
import { computeStats } from './rideRecorder';
import type { Ride } from './rideRecorder';

/** Subscriber-Bus für die UI: feuert wenn sich der Sync-State irgendeiner
 *  Ride geändert hat (push erfolgreich, queue-Eintrag erstellt/entfernt).
 *  MyRidesPage subscribed, um die Badges live zu aktualisieren. */
export const RIDE_SYNC_EVENT = 'adt:ride-sync-updated';
function dispatchSyncUpdated() {
  try {
    window.dispatchEvent(new CustomEvent(RIDE_SYNC_EVENT));
  } catch {
    /* SSR — egal */
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Queue-Ops
// ─────────────────────────────────────────────────────────────────────────

/** Markiert eine Ride als "muss hochgeladen werden". Idempotent. */
export async function enqueueRidePush(rideId: string): Promise<void> {
  const db = await getDb();
  // Bestehender 'delete'-Eintrag wird durch 'put' überschrieben — sinnvoll,
  // weil der User offenbar doch nicht löschen, sondern updaten will (z.B.
  // resume-after-discard). Wenn schon ein 'put' drin ist: einfach
  // queuedAt updaten + attempts zurücksetzen (nicht penalisieren für
  // weiteres Schreiben).
  await db.put('rideQueue', {
    rideId,
    op: 'put',
    queuedAt: new Date().toISOString(),
    attempts: 0,
  });
  dispatchSyncUpdated();
}

/** Markiert eine Ride als "muss am Server gelöscht werden". */
export async function enqueueRideDelete(rideId: string): Promise<void> {
  const db = await getDb();
  await db.put('rideQueue', {
    rideId,
    op: 'delete',
    queuedAt: new Date().toISOString(),
    attempts: 0,
  });
  dispatchSyncUpdated();
}

/** Sync-Status einer Ride: 'synced' = server hat den neuesten Stand,
 *  'pending' = lokaler Stand wartet auf Push, 'unknown' = nie gepusht
 *  und auch nicht in der Queue (z.B. pre-14cg Ride die noch nie
 *  enqueued wurde). */
export type RideSyncStatus = 'synced' | 'pending' | 'unknown';

export function rideSyncStatus(
  ride: Pick<Ride, 'updatedAt' | 'syncedAt'>,
  queued: boolean,
): RideSyncStatus {
  if (queued) return 'pending';
  if (ride.syncedAt) {
    // Wenn updatedAt nicht gesetzt ist aber syncedAt schon, gilt synced
    // (alte Rows die vor 14cg synchronisiert worden wären — aktuell unmöglich,
    // aber definsiv).
    if (!ride.updatedAt) return 'synced';
    return ride.updatedAt <= ride.syncedAt ? 'synced' : 'pending';
  }
  return 'unknown';
}

/** Liefert das Set aller Ride-IDs, die aktuell einen Queue-Eintrag haben.
 *  Wird von der UI gebraucht um Badges zu rendern. */
export async function listQueuedRideIds(): Promise<Set<string>> {
  try {
    const db = await getDb();
    const all = await db.getAll('rideQueue');
    return new Set(all.map((e) => e.rideId));
  } catch {
    return new Set();
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Flush — pending Pushes durchziehen
// ─────────────────────────────────────────────────────────────────────────

let flushPromise: Promise<void> | null = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

/** Plant einen Flush-Lauf in 800 ms ein. Mehrfache Calls werden coalesced.
 *  Wird von rideRecorder.stop()/delete() und vom 'online'-Listener
 *  aufgerufen. */
export function flushRideQueueSoon(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushRideQueue();
  }, 800);
}

/** Drained die rideQueue. Ist re-entrant-safe (zweiter paralleler Call
 *  awaitet das bestehende Promise). */
export async function flushRideQueue(): Promise<void> {
  if (flushPromise) return flushPromise;
  flushPromise = (async () => {
    try {
      const db = await getDb();
      const entries = await db.getAll('rideQueue');
      if (entries.length === 0) return;

      let didChange = false;
      for (const entry of entries) {
        try {
          if (entry.op === 'put') {
            await flushPut(entry.rideId);
          } else {
            await flushDelete(entry.rideId);
          }
          didChange = true;
        } catch (err) {
          // Markieren + drinlassen — nächster online-Event versucht's wieder.
          // 401-Fall hat flushPut/flushDelete als "still pending" markiert,
          // d.h. wir landen hier nicht für nicht-eingeloggte-Calls.
          const msg = err instanceof Error ? err.message : String(err);
          try {
            const cur = await db.get('rideQueue', entry.rideId);
            if (cur) {
              await db.put('rideQueue', {
                ...cur,
                attempts: cur.attempts + 1,
                lastError: msg.slice(0, 200),
              });
            }
          } catch {
            /* secondary IDB-Error — silent */
          }
          // Bei einem einzelnen Fehler weitermachen — die anderen Einträge
          // können ja noch durchgehen.
        }
      }

      if (didChange) dispatchSyncUpdated();
    } finally {
      flushPromise = null;
    }
  })();
  return flushPromise;
}

/** Pusht eine konkrete Ride. Wirft bei Fehlern (die nicht 401 sind). */
async function flushPut(rideId: string): Promise<void> {
  const db = await getDb();
  const ride = await db.get('rides', rideId);
  if (!ride) {
    // Ride wurde inzwischen lokal gelöscht — Queue-Eintrag wegwerfen, der
    // delete-Pfad sollte separat enqueuet sein.
    await db.delete('rideQueue', rideId);
    return;
  }

  // Aggregate berechnen — der Server bekommt die für effizientes Listing,
  // muss aber die points_json nicht parsen müssen.
  const stats = computeStats(ride.points);

  const res = await fetch(`/api/me/rides/${encodeURIComponent(rideId)}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ride: {
        id: ride.id,
        startedAt: ride.startedAt,
        endedAt: ride.endedAt ?? null,
        points: ride.points,
        distanceM: stats.distanceMeters,
        gainM: stats.elevationGainM,
        lossM: stats.elevationLossM,
        movingTimeMs: stats.movingTimeMs,
        durationMs: stats.durationMs,
      },
    }),
  });

  if (res.status === 401) {
    // Nicht eingeloggt — Eintrag drinlassen, NICHT als Fehler werten.
    // Nächster Login pullt + flushed.
    return;
  }
  if (!res.ok) {
    throw new Error(`push failed: HTTP ${res.status}`);
  }

  const now = new Date().toISOString();
  // syncedAt zurückschreiben + Queue-Eintrag löschen, atomar-ish (zwei
  // separate Calls, aber idempotent).
  const cur = await db.get('rides', rideId);
  if (cur) {
    await db.put('rides', { ...cur, syncedAt: now });
  }
  await db.delete('rideQueue', rideId);
}

/** Schickt einen DELETE für eine Ride zum Server. 404 wird als success
 *  behandelt (idempotent — Server hatte die Ride noch nie). */
async function flushDelete(rideId: string): Promise<void> {
  const res = await fetch(`/api/me/rides/${encodeURIComponent(rideId)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (res.status === 401) return; // siehe flushPut
  if (!res.ok && res.status !== 404) {
    throw new Error(`delete failed: HTTP ${res.status}`);
  }
  const db = await getDb();
  await db.delete('rideQueue', rideId);
}

// ─────────────────────────────────────────────────────────────────────────
// Pull — Server → IDB (cross-device merge)
// ─────────────────────────────────────────────────────────────────────────

interface ServerRideMeta {
  id: string;
  name?: string | null;
  startedAt: string;
  endedAt?: string | null;
  distanceM: number;
  gainM: number;
  lossM: number;
  movingTimeMs: number;
  durationMs: number;
  createdAt: string;
  updatedAt: string;
}

interface ServerRideFull extends ServerRideMeta {
  points: Array<{ lat: number; lon: number; ele?: number; t: number; speed?: number }>;
}

/** Holt alle Server-Rides (nur Meta) und merged in IDB. Server-Rides die
 *  lokal fehlen werden voll heruntergeladen (zweiter Roundtrip pro Ride).
 *  Wir machen das nicht parallel um keinen Server-Burst zu produzieren —
 *  jeder Pull holt typischerweise nur 0-5 fehlende Rides. */
export async function pullRides(): Promise<void> {
  let serverRides: ServerRideMeta[] = [];
  try {
    const res = await fetch('/api/me/rides', { credentials: 'include' });
    if (!res.ok) return; // 401 silent
    const data = (await res.json()) as { rides?: ServerRideMeta[] };
    serverRides = Array.isArray(data?.rides) ? data.rides : [];
  } catch {
    return; // offline
  }

  if (serverRides.length === 0) return;

  const db = await getDb();
  let imported = 0;
  for (const meta of serverRides) {
    const local = await db.get('rides', meta.id);
    // Strategy: wenn lokaler Stand fehlt → voll laden + speichern.
    // Wenn lokaler Stand vorhanden + lokaler updatedAt jünger als Server →
    // skip (lokaler Stand wird beim nächsten flush hochgepusht).
    // Sonst → voll laden + ersetzen.
    if (local) {
      const localStamp = local.updatedAt ?? local.endedAt ?? local.startedAt;
      if (localStamp && localStamp > meta.updatedAt) continue;
    }
    try {
      const fullRes = await fetch(
        `/api/me/rides/${encodeURIComponent(meta.id)}`,
        { credentials: 'include' },
      );
      if (!fullRes.ok) continue;
      const fullData = (await fullRes.json()) as { ride?: ServerRideFull };
      const full = fullData?.ride;
      if (!full || !Array.isArray(full.points)) continue;
      await db.put('rides', {
        id: full.id,
        startedAt: full.startedAt,
        endedAt: full.endedAt ?? undefined,
        points: full.points,
        updatedAt: full.updatedAt,
        // Pulled vom Server → per Definition synced. Kein neuer Push nötig.
        syncedAt: full.updatedAt,
      });
      imported++;
    } catch {
      /* einzelner Ride-Pull-Fehler — weiter zum nächsten */
    }
  }
  if (imported > 0) dispatchSyncUpdated();
}

// ─────────────────────────────────────────────────────────────────────────
// Background-Hooks
// ─────────────────────────────────────────────────────────────────────────

let installed = false;
/** Installiert die online-Listener + macht einen initialen Flush.
 *  Idempotent. App.tsx ruft das einmal auf, nach erfolgreicher Auth. */
export function installRideSyncBackground(): void {
  if (installed) return;
  installed = true;
  // Beim Reconnect die Queue durchziehen — analog zum elevationQueue/
  // cruxQueue-Pattern. Wichtig: NICHT bei jedem 'visibilitychange' o.ä.,
  // sonst schießen wir den Server bei jedem Tab-Switch.
  window.addEventListener('online', () => {
    flushRideQueueSoon();
  });
  // Initialer Flush kurz nach Mount — falls Queue-Einträge aus einer
  // vorherigen Session vorliegen.
  flushRideQueueSoon();
}
